import {
  breakpointSizes,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Box from 'src/components/Box/Box'
import EmptyLabel from 'src/components/EmptyLabel/EmptyLabel'
import RecurrenceFrequencyLabel from 'src/components/RecurrenceFrequencyLabel/RecurrenceFrequencyLabel'
import Tooltip from 'src/components/Tooltip/Tooltip'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes, ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import { CreditCardPermissionsModel } from 'src/models/creditCardPermissions.models'
import {
  RecurrenceModel,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { UserListModel } from 'src/models/user.models'
import Lnk from 'src/navigation/Lnk'
import {
  accountOverviewUrl,
  budgetOverviewUrl,
  peopleMembersDetailUrl,
} from 'src/navigation/urls'
import { css } from 'styled-components'

interface DetailItemProps {
  title: React.ReactElement
  item: React.ReactElement
}

const DetailItem: React.FC<DetailItemProps> = (props): React.ReactElement => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        margin-top: 36px;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            14,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {props.title}
      </span>
      <span
        css={css`
          margin-top: 6px;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {props.item}
      </span>
    </div>
  )
}

interface BudgetDetailsBoxProps {
  className?: string
  isInactive: boolean
  owner: UserListModel | undefined
  recipient: UserListModel | undefined
  fundingSource: string
  inactiveSince?: string
  activeUntil?: string
  createdAt: string
  canViewParent: boolean
  parentId?: string
  parentCreditCardPermissions?: CreditCardPermissionsModel
  last4: string
  recurrenceDetails?: {
    timezone: string
    recurrence: RecurrenceModel
    currency: ExtendCurrency
  }
}

const BudgetDetailsBox: React.FC<BudgetDetailsBoxProps> = (
  props,
): React.ReactElement => {
  const { analytics } = useAnalyticsContext()

  const trackBudgetCreatorClick = (): void => {
    analytics?.track(
      'budget detail overview - view budget creator - text link press',
      {
        intent: 'view budget creator',
        interaction: 'text link press',
        screen: budgetOverviewUrl(':id'),
      },
    )
  }

  const trackFundingSourceClick = (): void => {
    analytics?.track(
      'budget detail overview - view funding source - text link press',
      {
        intent: 'view funding sourcer',
        interaction: 'text link press',
        screen: budgetOverviewUrl(':id'),
      },
    )
  }

  const topContent = (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
      `}
    >
      <div
        css={css`
          display: flex;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          <Trns source={'Budget Details'} />
        </span>
      </div>
    </div>
  )

  const middleContent = (
    <div
      css={css`
        margin-bottom: 36px;
        ${getBreakpointUp(breakpointSizes.XSM)(css`
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr;
          column-gap: 40px;
          row-gap: 40px;
        `)}
        ${getBreakpointOnly(breakpointSizes.XXSM)(css`
          display: flex;
          flex-direction: column;
        `)}
      `}
    >
      <div>
        {props.owner && (
          <div
            css={css`
              display: flex;
              flex-direction: row;
            `}
          >
            <UserAvatar
              size={AvatarSizes.Large}
              user={props.owner}
              css={css`
                margin-top: 36px;
              `}
            />
            <div
              css={css`
                margin-left: 8px;
              `}
            >
              <DetailItem
                title={<Trns source={'Budget Creator'} />}
                item={
                  <Lnk
                    to={peopleMembersDetailUrl(props.owner.id)}
                    ariaLabelSource={`${props.owner.firstName} ${props.owner.lastName}`}
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.cta,
                        styleConstants.fontFamilies.regular,
                      )}
                      text-decoration: underline;
                    `}
                    onClick={trackBudgetCreatorClick}
                  >
                    {`${props.owner.firstName} ${props.owner.lastName}`}
                  </Lnk>
                }
              />
            </div>
          </div>
        )}
      </div>
      {!!props.canViewParent && !!props.parentId && (
        <DetailItem
          title={<Trns source={'Funding Source'} />}
          item={
            <span
              css={css`
                display: flex;
                flex-direction: row;
              `}
            >
              <Lnk
                to={accountOverviewUrl(props.parentId)}
                ariaLabelSource={`${props.fundingSource}`}
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.regular,
                  )}
                  text-decoration: underline;
                `}
                onClick={trackFundingSourceClick}
              >
                {props.fundingSource}
              </Lnk>
              <div
                css={css`
                  margin-left: 8px;
                `}
              >
                <Tag text={props.last4} size={TagSizes.Small} />
              </div>
            </span>
          }
        />
      )}
    </div>
  )

  const bottomContent = (
    <div
      css={css`
        border-top: 1px solid ${styleConstants.colors.gray_medium};
        ${getBreakpointUp(breakpointSizes.XSM)(css`
          display: grid;
          grid-template-columns: 1fr 1fr;
          column-gap: 40px;
        `)}
        ${getBreakpointOnly(breakpointSizes.XXSM)(css`
          display: flex;
          flex-direction: column;
        `)}
      `}
    >
      <DetailItem
        title={<Trns source={'Created On'} />}
        item={<>{props.createdAt}</>}
      />
      {!props.isInactive ? (
        <DetailItem
          title={<Trns source={'Active Until'} />}
          item={<>{props.activeUntil ? props.activeUntil : <EmptyLabel />}</>}
        />
      ) : (
        <DetailItem
          title={<Trns source={'Inactive Since'} />}
          item={
            <>
              <span
                css={css`
                  ${getFontCss(
                    16,
                    styleConstants.colors.red,
                    styleConstants.fontFamilies.regular,
                  )}
                  margin-top: 6px;
                `}
              >
                {props.inactiveSince}
              </span>
            </>
          }
        />
      )}
      {props.recurrenceDetails !== undefined && (
        <>
          <DetailItem
            title={<Trns source={'Frequency'} />}
            item={
              <RecurrenceFrequencyLabel
                recurrence={props.recurrenceDetails.recurrence}
                timezone={props.recurrenceDetails.timezone}
              />
            }
          />
          {(props.recurrenceDetails.recurrence.remainingCount !== undefined ||
            props.recurrenceDetails.recurrence.terminator ===
              RecurrenceTerminator.None) && (
            <>
              {props.recurrenceDetails.recurrence.terminator ===
                RecurrenceTerminator.None ||
              (props.recurrenceDetails.recurrence.remainingCount !==
                undefined &&
                props.recurrenceDetails.recurrence.remainingCount > 0) ? (
                <DetailItem
                  title={<Trns source={'Next Reset'} />}
                  item={
                    <>
                      {props.recurrenceDetails.recurrence.nextRecurrenceAtTimezonedString(
                        props.recurrenceDetails.timezone,
                      ) !== undefined &&
                        props.recurrenceDetails.recurrence.nextRecurrenceAtTimezonedString(
                          props.recurrenceDetails.timezone,
                        )}
                    </>
                  }
                />
              ) : (
                <DetailItem
                  title={<Trns source={'Last Reset'} />}
                  item={
                    <>
                      {props.recurrenceDetails.recurrence.remainingCount ===
                        0 &&
                        (props.recurrenceDetails.recurrence.prevRecurrenceAtTimezonedString(
                          props.recurrenceDetails.timezone,
                        ) !== undefined ? (
                          props.recurrenceDetails.recurrence.prevRecurrenceAtTimezonedString(
                            props.recurrenceDetails.timezone,
                          )
                        ) : (
                          <EmptyLabel />
                        ))}
                    </>
                  }
                />
              )}
            </>
          )}
          <DetailItem
            title={
              <>
                <Trns source={'Projected Budget'} />
                <Tooltip
                  openLeft={false}
                  css={css`
                    height: auto;
                    display: inline-block;
                    vertical-align: middle;
                  `}
                >
                  <p>
                    <Trns
                      source={
                        '${projectedBudget} is the total value of the Budget over its future number of resets. This value will change with every update to the Budget limit or Budget reset frequency.'
                      }
                      expressions={{
                        projectedBudget: (
                          <span
                            css={css`
                              font-weight: bold;
                            `}
                          >
                            <Trns source={'Projected Budget'} />
                          </span>
                        ),
                      }}
                    />
                  </p>
                </Tooltip>
              </>
            }
            item={
              <>
                {props.recurrenceDetails.recurrence.remainingCount !==
                  undefined &&
                props.recurrenceDetails.recurrence.remainingCount > 0 ? (
                  <Trns
                    source={'${amount} after ${remainingCount} resets'}
                    expressions={{
                      amount: (
                        <Crncy
                          currency={props.recurrenceDetails.currency}
                          amount={
                            props.recurrenceDetails.recurrence.balance *
                            props.recurrenceDetails.recurrence.remainingCount
                          }
                        />
                      ),
                      remainingCount: `${props.recurrenceDetails.recurrence.remainingCount}`,
                    }}
                  />
                ) : (
                  <EmptyLabel />
                )}
              </>
            }
          />
        </>
      )}
    </div>
  )

  return (
    <Box
      noPadding
      css={css`
        padding: 40px 40px;
      `}
    >
      <div
        css={css`
          height: 100%;
        `}
      >
        {topContent}
        {middleContent}
        {bottomContent}
      </div>
    </Box>
  )
}

export default BudgetDetailsBox
